// Waitlist Email Service
import { getApiUrl } from '../config/api';
import { getLocationDataSecure, LocationData } from './locationService';

export interface WaitlistEntry {
  email: string;
  timestamp: Date;
  source: 'soft-launch' | 'landing' | 'other';
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
}

export const addToWaitlist = async (email: string): Promise<boolean> => {
  try {
    // Get location data before sending request
    const locationData: LocationData = await getLocationDataSecure();
    
    const response = await fetch(getApiUrl('/waitlist/subscribe'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        source: 'soft-launch',
        ...locationData, // Include location data
      }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      return true;
    } else if (data.message === 'This email is already on our waitlist!') {
      // Email already exists - still consider this a "success" for UX
      return true;
    } else {
      throw new Error(data.message || 'Failed to join waitlist');
    }
  } catch (error) {
    console.error('Error adding to waitlist:', error);
    
    // Fallback: Store in localStorage for now (including location if available)
    const waitlistEntries = getLocalWaitlist();
    const newEntry: WaitlistEntry = {
      email,
      timestamp: new Date(),
      source: 'soft-launch',
    };
    
    // Try to get location data for fallback storage too
    try {
      const locationData = await getLocationDataSecure();
      Object.assign(newEntry, locationData);
    } catch (locationError) {
      console.warn('Could not add location to fallback entry:', locationError);
    }
    
    waitlistEntries.push(newEntry);
    localStorage.setItem('waitlist_entries', JSON.stringify(waitlistEntries));
    return true;
  }
};

export const getLocalWaitlist = (): WaitlistEntry[] => {
  try {
    const stored = localStorage.getItem('waitlist_entries');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const getWaitlistCount = async (): Promise<number> => {
  try {
    const response = await fetch(getApiUrl('/waitlist/count'));
    if (response.ok) {
      const data = await response.json();
      // Add base number to make it look more impressive
      return (data.count || 0) + 4200; 
    }
  } catch (error) {
    console.error('Error fetching waitlist count:', error);
  }
  
  // Fallback: Use local count + base number
  const localEntries = getLocalWaitlist();
  return 4247 + localEntries.length;
};

export default {
  addToWaitlist,
  getWaitlistCount,
  getLocalWaitlist
};
